import React from 'react';
import ReactDOM from 'react-dom/client';
import App from './App';

const root = ReactDOM.createRoot(document.getElementById('root'));
root.render(<App />);

// ---------------------------------------------------------------------------
// PWA Service Worker registration (client/public/sw.js)
// ---------------------------------------------------------------------------
// We register ONLY when safe:
//  - production build
//  - secure context (https or localhost)
//  - document is "active" (not prerendering / not hidden)
//
// This avoids Chrome/Android InvalidStateError:
//   "Failed to register a ServiceWorker: The document is in an invalid state"
// ---------------------------------------------------------------------------

const isProd = process.env.NODE_ENV === 'production';
const hasSW = typeof navigator !== 'undefined' && 'serviceWorker' in navigator;

function normalizePublicUrl(raw) {
  const v = (raw || '').trim();
  if (v === '/' || v === './') return '';
  return v.replace(/\/$/, '');
}

function isSecureContextNow() {
  // window.isSecureContext is correct for https; allow localhost for dev.
  const host = window.location.hostname;
  const isLocalhost = host === 'localhost' || host === '127.0.0.1' || host === '::1';
  return window.isSecureContext || isLocalhost;
}

function waitForVisible() {
  if (document.visibilityState === 'visible') return Promise.resolve();
  return new Promise((resolve) => {
    const onVis = () => {
      if (document.visibilityState === 'visible') {
        document.removeEventListener('visibilitychange', onVis);
        resolve();
      }
    };
    document.addEventListener('visibilitychange', onVis);
  });
}

function waitForPrerenderingDone() {
  // Chrome Prerender2: document.prerendering boolean + event 'prerenderingchange'
  if (!('prerendering' in document)) return Promise.resolve();
  if (!document.prerendering) return Promise.resolve();
  return new Promise((resolve) => {
    const onChange = () => {
      if (!document.prerendering) {
        document.removeEventListener('prerenderingchange', onChange);
        resolve();
      }
    };
    document.addEventListener('prerenderingchange', onChange, { once: true });
  });
}

async function registerSW() {
  try {
    if (!isProd || !hasSW) return;

    if (!isSecureContextNow()) {
      // avoid keeping stale SW when context is unsafe
      try {
        const regs = await navigator.serviceWorker.getRegistrations();
        await Promise.all(regs.map((r) => r.unregister()));
      } catch {}
      return;
    }

    const base = normalizePublicUrl(process.env.PUBLIC_URL);
    const swUrl = `${base}/sw.js`;
    const scope = base ? `${base}/` : '/';

    // Clean up stale registrations from older scopes (helps after redeploys / path changes)
    try {
      const expectedScopeAbs = new URL(scope, window.location.origin).href;
      const regs = await navigator.serviceWorker.getRegistrations();
      await Promise.all(
        regs
          .filter((r) => r && r.scope && r.scope !== expectedScopeAbs)
          .map((r) => r.unregister().catch(() => {}))
      );
    } catch {}

    // Wait until the page is truly "active" (prevents InvalidStateError)
    await waitForPrerenderingDone();
    await waitForVisible();

    // Light preflight: if sw.js is being rewritten to HTML, registration will fail.
    // (This is commonly caused by Amplify SPA rewrites catching /sw.js)
    try {
      const resp = await fetch(swUrl, { cache: 'no-store' });
      if (resp.ok) {
        const head = (await resp.text()).slice(0, 120).toLowerCase();
        if (head.includes('<!doctype') || head.includes('<html')) {
          console.warn('[PWA] sw.js appears to be HTML (SPA rewrite). Fix Amplify rewrites for', swUrl);
          return;
        }
      }
    } catch {
      // ignore; registration may still succeed
    }

    try {
      const reg = await navigator.serviceWorker.register(swUrl, { scope });
      // Auto-reload when a new SW takes control (prevents stale JS on mobile PWAs)
      try {
        let refreshing = false;
        navigator.serviceWorker.addEventListener('controllerchange', () => {
          if (refreshing) return;
          refreshing = true;
          window.location.reload();
        });
        reg.onupdatefound = () => {
          const iw = reg.installing;
          if (!iw) return;
          iw.onstatechange = () => {
            if (iw.state === "installed" && navigator.serviceWorker.controller) {
              // New content available; SW already calls skipWaiting+claim
              // controllerchange will trigger reload.
            }
          };
        };
      } catch {}
      return reg;
    } catch (err) {
      // One retry for InvalidStateError (Chrome can still throw during BFCache/prerender edge cases)
      const name = err?.name || '';
      const msg = String(err?.message || '');
      const isInvalidState = name === 'InvalidStateError' || /invalid state/i.test(msg);
      if (isInvalidState) {
        try {
          await new Promise((r) => setTimeout(r, 300));
          await waitForPrerenderingDone();
          await waitForVisible();
          await navigator.serviceWorker.register(swUrl, { scope });
          return;
        } catch (err2) {
          console.warn('[PWA] Service worker registration failed (retry):', err2);
          return;
        }
      }

      console.warn('[PWA] Service worker registration failed:', err);
    }
  } catch (e) {
    console.warn('[PWA] SW registration exception:', e);
  }
}

if (isProd && hasSW) {
  window.addEventListener('load', () => {
    registerSW().catch(() => {});
  });
} else {
  // Ensure we don't keep a stale SW during local/dev or insecure contexts.
  if (hasSW) {
    navigator.serviceWorker.getRegistrations()
      .then((regs) => Promise.all(regs.map((r) => r.unregister())))
      .catch(() => {});
  }
}